import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ClientCatalogItem {
  cdClient: number;
  dsNombreFantasia?: string;
  dsRazonSocial?: string;
  dsCUIT?: string;
  isEstado?: boolean;
}

export interface ClientCatalogPage {
  data: ClientCatalogItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

/**
 * Catálogo único de clientes (N1): paginación, búsqueda y normalización de la
 * respuesta en un solo lugar. Los componentes consumen este servicio en vez de
 * repetir la carga contra /api/Client/GetAllByServicesPaged.
 *
 * - search(): una página, para autocompletar/búsqueda.
 * - getAll(): todas las páginas en secuencia, para selects.
 * - La cancelación de respuestas fuera de orden la maneja el consumidor con
 *   switchMap/takeUntil (los observables son fríos y se cancelan al desuscribirse).
 */
@Injectable({ providedIn: 'root' })
export class ClientCatalogService {

  private readonly endpoint = `${environment.apiBaseUrl}/api/Client/GetAllByServicesPaged`;

  constructor(private http: HttpClient) {
  }

  search(serviceNames: string[], search: string, pageNumber = 1, pageSize = 20): Observable<ClientCatalogPage> {
    const params = new HttpParams()
      .set('serviceNames', serviceNames.join(','))
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('search', (search || '').trim());

    return this.http.get<any>(this.endpoint, { params }).pipe(
      map(response => this.normalizePage(response))
    );
  }

  getAll(serviceNames: string[], pageSize = 100): Observable<ClientCatalogItem[]> {
    return this.search(serviceNames, '', 1, pageSize).pipe(
      expand(page => this.hasMorePages(page)
        ? this.search(serviceNames, '', page.pageNumber + 1, pageSize)
        : EMPTY),
      reduce((accumulated, page) => accumulated.concat(page.data), [] as ClientCatalogItem[])
    );
  }

  private hasMorePages(page: ClientCatalogPage): boolean {
    return page.data.length > 0 && (page.pageNumber * page.pageSize) < page.totalCount;
  }

  private normalizePage(response: any): ClientCatalogPage {
    const data: ClientCatalogItem[] = Array.isArray(response) ? response : (response?.data || []);
    return {
      data,
      pageNumber: response?.pageNumber ?? 1,
      pageSize: response?.pageSize ?? data.length,
      totalCount: response?.totalCount ?? data.length,
    };
  }
}
